/**
 * useBilling — fetches the two billing tables independently so one endpoint
 * failing does not hide the other's data. Exposes reload() for a panel retry.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchDailyUsage,
  fetchRecentEpisodes,
  type DailyUsageRow,
  type RecentEpisodeRow,
} from "../../../tauri/billing";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";

export interface UseBillingResult {
  readonly daily: readonly DailyUsageRow[];
  readonly episodes: readonly RecentEpisodeRow[];
  readonly dailyLoading: boolean;
  readonly episodesLoading: boolean;
  readonly dailyError: string | null;
  readonly episodesError: string | null;
  readonly reloadDaily: () => Promise<void>;
  readonly reloadEpisodes: () => Promise<void>;
}

function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : String(reason);
}

export function useBilling(): UseBillingResult {
  const runtime = useAppRuntime();
  const baseUrl = runtime.transport.baseUrl;
  const [daily, setDaily] = useState<readonly DailyUsageRow[]>([]);
  const [episodes, setEpisodes] = useState<readonly RecentEpisodeRow[]>([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [episodesLoading, setEpisodesLoading] = useState(true);
  const [dailyError, setDailyError] = useState<string | null>(null);
  const [episodesError, setEpisodesError] = useState<string | null>(null);
  const loadedRef = useRef(false);

  const reloadDaily = useCallback(async (): Promise<void> => {
    setDailyLoading(true);
    try {
      const rows = await fetchDailyUsage(baseUrl, {});
      setDaily(rows);
      setDailyError(null);
    } catch (e) {
      setDailyError(errorMessage(e));
    } finally {
      setDailyLoading(false);
    }
  }, [baseUrl]);

  const reloadEpisodes = useCallback(async (): Promise<void> => {
    setEpisodesLoading(true);
    try {
      const rows = await fetchRecentEpisodes(baseUrl, { limit: 50 });
      setEpisodes(rows);
      setEpisodesError(null);
    } catch (e) {
      setEpisodesError(errorMessage(e));
    } finally {
      setEpisodesLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void reloadDaily();
    void reloadEpisodes();
  }, [reloadDaily, reloadEpisodes]);

  return {
    daily,
    episodes,
    dailyLoading,
    episodesLoading,
    dailyError,
    episodesError,
    reloadDaily,
    reloadEpisodes,
  };
}
